import { useContext, useEffect } from "react";
import { MetaType } from "core/enums";

import {
  DateCell,
  DefaultHeader,
  TruncatedCell
} from "components/SharedTable/tableRenderers";

import {
  CASE_TABLE_COLUMNS,
  CONDITIONALLY_HIDDEN_COLUMNS,
  CONSTANT_HIDDEN_COLUMNS,
  INITIAL_HIDDEN_COLUMNS
} from "components/CasesTable/CaseManagementTableColumns";

import { FeaturesContext } from "contexts/FeaturesContext";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";

export function useGetCaseTableColumns(setTableState) {
  const { hasFeature } = useContext(FeaturesContext);

  const { loading, error, response: metaTypeResponse } = useFetchGetV2(
    "case_meta_types_expanded"
  );

  useEffect(() => {
    if (!metaTypeResponse) return;

    const metaColumns = setMetaColumns(metaTypeResponse?.results);

    let conditionallyHiddenColumns = [];
    let initialHiddenColumns = INITIAL_HIDDEN_COLUMNS;
    let columns = CASE_TABLE_COLUMNS;

    if (hasFeature("work_orders")) {
      conditionallyHiddenColumns = CONDITIONALLY_HIDDEN_COLUMNS;
    } else {
      columns = CASE_TABLE_COLUMNS.filter(
        ({ id }) => !CONDITIONALLY_HIDDEN_COLUMNS.includes(id)
      );
    }

    setTableState(state => {
      let hiddenColumns = state.hiddenColumns;

      if (hiddenColumns.length === 0) {
        hiddenColumns = [
          ...CONSTANT_HIDDEN_COLUMNS,
          ...conditionallyHiddenColumns,
          ...initialHiddenColumns,
          ...metaColumns.map(({ id }) => id)
        ];
      }

      return {
        ...state,
        columns: [...columns, ...metaColumns],
        hiddenColumns: hiddenColumns
      };
    });
  }, [metaTypeResponse, hasFeature, setTableState]);

  return { loading, error };
}

function setMetaColumns(results) {
  const metaColumns =
    results?.map(({ name, data_type }) => {
      const attribute = name
        .split(" ")
        .join("_")
        .replace("(", "")
        .replace(")", "")
        .toLowerCase();

      const column = {
        Header: data =>
          DefaultHeader({
            sortBy: data.state.sortBy,
            column: data.column,
            header: name
          }),
        title: name,
        id: attribute,
        accessor: data => {
          if (
            data.attributes[attribute]?.value &&
            data_type?.id === MetaType.DATE
          ) {
            // convert unix time to date and return date in format (2024-05-24) as accessor
            const unixTimeStamp = data.attributes[attribute]?.value;

            const date = new Date(unixTimeStamp * 1000)
              .toISOString()
              .split("T")[0];

            return date ?? null;
          }
          return data.attributes[attribute]?.value ?? null;
        },
        Cell: data => {
          if (
            (data.row.isGrouped && !data.cell.isGrouped) ||
            !data.cell.value
          ) {
            return null;
          }

          // If grouped row then return date
          if (data.row.isGrouped) {
            return data_type?.id === MetaType.DATE
              ? DateCell({ value: data.cell?.value })
              : TruncatedCell({ value: data.cell?.value });
          }

          // If not grouped and date type then pass datestring to DateCell
          return data_type?.id === MetaType.DATE
            ? DateCell({
                value: data.row.original.attributes[attribute].value * 1000
              })
            : TruncatedCell({ value: data.cell?.value });
        }
      };

      return column;
    }) ?? [];

  return metaColumns;
}
